import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { toast } from "sonner";
import { getOrCreateForwardAddress, listForwardedEmails } from "@/server/forward-inbox";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const FORWARD_KEY = ["forward-address"] as const;
const EMAILS_KEY = ["forwarded-emails"] as const;

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

export function ForwardInboxCard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const { data: addressData, isLoading: addrLoading } = useQuery({
    queryKey: FORWARD_KEY,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      return getOrCreateForwardAddress({ headers });
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const { data: emailsData, isLoading: emailsLoading } = useQuery({
    queryKey: EMAILS_KEY,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      return listForwardedEmails({ headers });
    },
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const refresh = useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
    },
  });

  const address = addressData?.address ?? "";

  const handleCopy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success("Address copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Card variant="outlined" sx={{ borderColor: "primary.light" }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
            <ForwardToInboxIcon color="primary" sx={{ mt: 0.5 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Forward rejection emails to JobYoda
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Forward (or auto-forward) recruiter emails to your private address. We'll classify
                them and link rejections to the right application automatically.
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: "stretch" }}>
            <TextField
              value={addrLoading ? "Loading…" : address}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  readOnly: true,
                  sx: { fontFamily: "monospace", fontSize: 14 },
                },
              }}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
            />
            <Tooltip title={copied ? "Copied!" : "Copy address"}>
              <span>
                <Button
                  variant="contained"
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={!address}
                  sx={{ minWidth: 120 }}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <Button
            size="small"
            onClick={() => setSetupOpen((o) => !o)}
            endIcon={setupOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: "flex-start" }}
          >
            How to set up auto-forwarding
          </Button>

          <Collapse in={setupOpen} timeout="auto" unmountOnExit>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Gmail" />
                <Tab label="Outlook" />
                <Tab label="Yahoo" />
              </Tabs>
              {tab === 0 && <GmailSteps />}
              {tab === 1 && <OutlookSteps />}
              {tab === 2 && <YahooSteps />}
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Tip: filter on subject keywords like "unfortunately", "not moving forward", or
                "other candidates" so only rejections get forwarded.
              </Typography>
            </Box>
          </Collapse>

          <Divider />

          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Recent forwarded emails
            </Typography>
            <Button size="small" onClick={() => refresh.mutate()} disabled={emailsLoading}>
              Refresh
            </Button>
          </Stack>

          {emailsLoading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (emailsData?.emails ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No emails forwarded yet. Send a test email to your address above to try it out.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {(emailsData?.emails ?? []).slice(0, 5).map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.25,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
                      {e.subject || "(no subject)"}
                    </Typography>
                    {e.rejection_signal === "Rejected" ? (
                      <Chip size="small" color="error" label="Rejection" />
                    ) : (
                      <Chip size="small" variant="outlined" label="Other" />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                      {e.from_address}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(e.received_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                    {e.application_id && (
                      <Chip size="small" variant="outlined" color="primary" label="Linked" />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function GmailSteps() {
  return (
    <Stack spacing={1}>
      <Typography variant="body2"><b>1.</b> In Gmail, click the gear icon → <i>See all settings</i> → <i>Filters and Blocked Addresses</i>.</Typography>
      <Typography variant="body2"><b>2.</b> Click <i>Create a new filter</i>. In the <i>Has the words</i> field, paste:</Typography>
      <Box sx={{ fontFamily: "monospace", fontSize: 13, bgcolor: "action.hover", p: 1, borderRadius: 0.5 }}>
        unfortunately OR "not moving forward" OR "other candidates" OR "not selected" OR "regret to inform"
      </Box>
      <Typography variant="body2"><b>3.</b> Click <i>Create filter</i> → check <i>Forward it to</i> → <i>Add forwarding address</i> and paste your JobYoda address.</Typography>
      <Typography variant="body2"><b>4.</b> Gmail will email a confirmation code to your JobYoda address. Wait ~10 sec, then come back here — it'll appear in <i>Recent forwarded emails</i> below. Copy the code into Gmail to confirm.</Typography>
      <Typography variant="body2"><b>5.</b> Save the filter. Done.</Typography>
    </Stack>
  );
}

function OutlookSteps() {
  return (
    <Stack spacing={1}>
      <Typography variant="body2"><b>1.</b> In Outlook, go to <i>Settings</i> (gear) → <i>Mail</i> → <i>Rules</i>.</Typography>
      <Typography variant="body2"><b>2.</b> Click <i>Add new rule</i>. Name it "Forward rejections".</Typography>
      <Typography variant="body2"><b>3.</b> <i>Add a condition</i> → <i>Subject or body includes</i> → add words like <code>unfortunately</code>, <code>not moving forward</code>, <code>other candidates</code>.</Typography>
      <Typography variant="body2"><b>4.</b> <i>Add an action</i> → <i>Forward to</i> → paste your JobYoda address.</Typography>
      <Typography variant="body2"><b>5.</b> Save. Done.</Typography>
    </Stack>
  );
}

function YahooSteps() {
  return (
    <Stack spacing={1}>
      <Typography variant="body2"><b>1.</b> In Yahoo Mail, click <i>Settings</i> → <i>More Settings</i> → <i>Filters</i>.</Typography>
      <Typography variant="body2"><b>2.</b> Click <i>Add new filters</i>. Set name "Forward rejections".</Typography>
      <Typography variant="body2"><b>3.</b> Under <i>Subject</i>, choose <i>contains</i> and add keywords like <code>unfortunately</code>, <code>not moving forward</code>.</Typography>
      <Typography variant="body2"><b>4.</b> Yahoo doesn't allow direct forwarding from filters on free accounts — instead, set up <i>Settings → More Settings → Mailboxes → Forwarding</i> to your JobYoda address.</Typography>
      <Typography variant="body2"><b>5.</b> Confirm by entering the code from the verification email that lands here.</Typography>
    </Stack>
  );
}
